import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { StaffAttendanceApiService, PeriodBucketItem, PeriodListBuckets } from '../services/staff-attendance-api.service';
import { StaffClassAttendanceNavState } from '../class-attendance/staff-class-attendance.component';
import { StaffSessionService } from '../services/staff-session.service';

const DATE_FORMAT = 'YYYY-MM-DD';

// The period-picker step for period-based classes (attendanceType !==
// 'DAY') — reached from StaffClassOverviewComponent.openTile('attendance').
// Only Ongoing and Completed period cards are tappable; Upcoming cards
// can't have attendance marked for a period that hasn't started yet.
@Component({
  selector: 'app-staff-period-wise-attendance',
  templateUrl: './staff-period-wise-attendance.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-period-wise-attendance.component.css']
})
export class StaffPeriodWiseAttendanceComponent implements OnInit {
  classId: string | null;
  staffId: string | number | null = null;

  selectedDateObj: Date = new Date();
  maxDate: Date = new Date();

  onGoingPeriods: PeriodBucketItem[] = [];
  upcomingPeriods: PeriodBucketItem[] = [];
  completedPeriods: PeriodBucketItem[] = [];

  loading = true;
  loadError = false;
  readonly skeletonPlaceholders = Array.from({ length: 3 }, () => ({}));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffAttendanceApi: StaffAttendanceApiService,
    private staffSession: StaffSessionService
  ) {}

  get selectedDate(): string {
    return moment(this.selectedDateObj).format(DATE_FORMAT);
  }

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    const staffDetails = this.staffSession.getStaffDetails();
    this.staffId = staffDetails?.staffId ?? null;
    this.maxDate = new Date();
    this.loadPeriodList();
  }

  onDateChange() {
    this.loadPeriodList();
  }

  goBack() {
    this.router.navigate(['/staff/class', this.classId]);
  }

  async loadPeriodList() {
    if (!this.staffId || !this.classId) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffAttendanceApi.getPeriodListBuckets(String(this.staffId), this.classId, this.selectedDate)
      );
      if (response?.status && response.body) {
        const buckets: PeriodListBuckets | undefined = response.body.periodListData?.[0];
        this.onGoingPeriods = buckets?.onGoingClassesData || [];
        this.upcomingPeriods = buckets?.upcomingClassesData || [];
        this.completedPeriods = buckets?.completedClassesData || [];
      } else {
        this.loadError = true;
      }
    } catch {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  get hasNoPeriods(): boolean {
    return !this.onGoingPeriods.length && !this.upcomingPeriods.length && !this.completedPeriods.length;
  }

  openPeriod(period: PeriodBucketItem) {
    const state: StaffClassAttendanceNavState = {
      periodId: period.periodId,
      selectedDate: this.selectedDate,
    };
    this.router.navigate(['/staff/class', this.classId, 'attendance'], { state });
  }

  // ---------- Card display fallbacks (per spec) ----------

  subjectLabel(p: PeriodBucketItem): string {
    return p.subject || 'notAssigned';
  }

  durationLabel(p: PeriodBucketItem): string {
    return p.periodDuration || 'notApplicableShort';
  }

  teacherNameLabel(p: PeriodBucketItem): string {
    return p.staffName || 'notAvailable';
  }

  hasTimeRange(p: PeriodBucketItem): boolean {
    return !!(p.periodStartTime && p.periodEndTime);
  }

  // Ongoing/Completed cards: "Marked" once attendance is taken, else "Tap
  // to mark". Upcoming cards always show "Tap to mark" even though they
  // aren't actually tappable — matches the spec exactly, misleading label
  // and all, since that's the mobile app's real (if odd) behaviour.
  statusLabel(p: PeriodBucketItem): string {
    return p.isAttendanceMarked ? 'attendanceMarked' : 'tapToMark';
  }
}
